
import { zip as observableZip, Subscription } from 'rxjs';
import { Component, OnInit, ViewChild, ViewEncapsulation, AfterViewInit, OnDestroy } from '@angular/core';
import { SubscriptionService } from '../../services/subscription.service';
import { AccountTypeOverview, GetSubscriptionInfoDto } from '../../models/subscriptions/subscription.models';
import { PaymentMethod } from '../../models/subscriptions/subscription.enums';
import { LoggedInUserService } from '../../auth/logged.in.user';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { PaymentService } from '../../services/payment.service';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { CmsDialogService } from '../../services/cms.dialog.service';
import { CmsDialogDto } from '../../models/cms.dialogs/cms.dialogs.models';
import { PLAN_DISPLAY_ORDER, PlanKind } from './subscription-plan.data';

export interface SubscriptionPlans {
    low: AccountTypeOverview;
    high: AccountTypeOverview;
    combined: AccountTypeOverview;
}

@Component({
    selector: 'subscribe',
    template: require('./subscribe.component.html'),
    styles: [require('./subscribe.component.css'), require('../account.management/personal.account.shared.css')],
    encapsulation: ViewEncapsulation.None
})
export class SubscribeComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly paymentMethods = PaymentMethod;

    plans: Partial<SubscriptionPlans> = {};
    paymentMethod: PaymentMethod;
    errors: string;
    selectedAccountId: number;
    selectedPlanKind: PlanKind;
    hideCardPayment: boolean;
    agreedToTandCs: boolean;
    paymentInfoBody: string;
    combinedSavingsAmount = 0;
    showCheckout = false;
    isLoading = true;

    poNumber: string;

    existingId: number;
    routeParamsSub: Subscription;
    queryParamsSub: Subscription;
    renewalPayByCard = false;

    @ViewChild('cardPayment', { static: false }) private cardPayment: any;
    @BlockUI() blockUi: NgBlockUI;

    cardOnly = false;

    private allAccountTypes: AccountTypeOverview[] = [];
    private readonly selectedAccount = 'selectedAccount';
    private lastResetCheckoutToken: string = null;

    constructor(
        private subscriptionService: SubscriptionService,
        private loggedInUserService: LoggedInUserService,
        private readonly cmsDialogService: CmsDialogService,
        private router: Router,
        private authService: AuthService,
        private paymentService: PaymentService,
        private route: ActivatedRoute
    ) {
        this.paymentMethod = PaymentMethod.Card;
        this.errors = '';
    }

    ngOnInit() {
        document.body.classList.add('personal-account-fit');

        if (this.routeParamsSub) {
            this.routeParamsSub.unsubscribe();
        }
        if (this.queryParamsSub) {
            this.queryParamsSub.unsubscribe();
        }

        this.renewalPayByCard = this.route.snapshot.queryParams['pay'] === 'card';

        this.queryParamsSub = this.route.queryParams.subscribe(query => {
            this.renewalPayByCard = query['pay'] === 'card';

            const resetCheckoutToken = query['resetCheckout'];
            if (resetCheckoutToken && resetCheckoutToken !== this.lastResetCheckoutToken) {
                this.lastResetCheckoutToken = resetCheckoutToken;
                this.resetToPlanSelection();
            }
        });

        this.routeParamsSub = this.route.params.subscribe(params => {
            this.existingId = +params['id'];
            this.renewalPayByCard = this.route.snapshot.queryParams['pay'] === 'card';

            const accountTypesObservable = this.subscriptionService.getAccountTypes();

            if (this.existingId) {
                const getSubscriptionObservable = this.subscriptionService.getSubscriptionInfo(this.existingId);

                observableZip(
                    accountTypesObservable,
                    getSubscriptionObservable,
                    (accountTypes: Array<AccountTypeOverview>, subscription: GetSubscriptionInfoDto) =>
                        ({ accountTypes, subscription })
                ).subscribe(results => {
                    this.populateAccountTypes(results.accountTypes);
                    this.selectByAccountTypeId(results.subscription.accountTypeId, true);
                    if (this.renewalPayByCard) {
                        this.paymentMethod = PaymentMethod.Card;
                    }
                });
            } else {
                this.renewalPayByCard = false;
                accountTypesObservable.subscribe((response: Array<AccountTypeOverview>) => {
                    this.populateAccountTypes(response);
                });
            }
        });

        this.cmsDialogService.getByDescription('PaymentInfo').subscribe((result: CmsDialogDto) => {
            this.paymentInfoBody = result.body;
        });

        $('#t-and-c-modal').modal();
        $('#t-and-c-modal').modal('hide');
    }

    ngOnDestroy() {
        document.body.classList.remove('personal-account-fit');
        if (this.routeParamsSub) {
            this.routeParamsSub.unsubscribe();
        }
        if (this.queryParamsSub) {
            this.queryParamsSub.unsubscribe();
        }
    }

    get showCardPaymentOnly(): boolean {
        return this.cardOnly || this.renewalPayByCard;
    }

    get pageTitle(): string {
        return this.renewalPayByCard ? 'Pay by card' : 'Subscription Plans';
    }

    ngAfterViewInit() {
    }

    get hasPlans(): boolean {
        return this.visiblePlanKinds.length > 0;
    }

    get visiblePlanKinds(): PlanKind[] {
        return PLAN_DISPLAY_ORDER.filter((k) => !!this.plans[k]);
    }

    getAccountTypeForKind(kind: PlanKind): AccountTypeOverview {
        return this.plans[kind];
    }

    isSelectedKind(kind: PlanKind): boolean {
        return this.selectedPlanKind === kind;
    }

    private static explicitPlanKind(accountType: AccountTypeOverview): PlanKind | null {
        const name = (accountType.name || '').toLowerCase();

        if (/\bcombined\b/i.test(name) || /\bbest\s*value\b/i.test(name)) {
            return 'combined';
        }
        if (/\bhigh\s*value\b/i.test(name)) {
            return 'high';
        }
        if (/\blow\s*value\b/i.test(name)) {
            return 'low';
        }
        return null;
    }

    private static isAssigned(plans: Partial<SubscriptionPlans>, accountType: AccountTypeOverview): boolean {
        return (
            plans.low === accountType ||
            plans.high === accountType ||
            plans.combined === accountType
        );
    }

    private static assignToEmptySlot(
        plans: Partial<SubscriptionPlans>,
        kind: PlanKind,
        accountType: AccountTypeOverview
    ): void {
        if (!plans[kind]) {
            plans[kind] = accountType;
        }
    }

    private buildPlansFromApi(dtos: Array<AccountTypeOverview>): Partial<SubscriptionPlans> {
        const plans: Partial<SubscriptionPlans> = {};
        const visible = (dtos || []).filter((t) => !t.hidden);
        const unmapped: AccountTypeOverview[] = [];

        for (const accountType of visible) {
            const kind = SubscribeComponent.explicitPlanKind(accountType);
            if (kind && !plans[kind]) {
                plans[kind] = accountType;
            } else {
                unmapped.push(accountType);
            }
        }

        for (const accountType of unmapped) {
            if (SubscribeComponent.isAssigned(plans, accountType)) {
                continue;
            }
            const kind = SubscribeComponent.explicitPlanKind(accountType);
            if (kind) {
                SubscribeComponent.assignToEmptySlot(plans, kind, accountType);
            }
        }

        const stillUnmapped = unmapped.filter((t) => !SubscribeComponent.isAssigned(plans, t));

        if (stillUnmapped.length > 0 && visible.length === 3) {
            const byPrice = [...visible].sort((a, b) => a.price - b.price);
            if (!plans.low && byPrice[0]) {
                plans.low = byPrice[0];
            }
            if (!plans.high && byPrice[1]) {
                plans.high = byPrice[1];
            }
            if (!plans.combined && byPrice[2]) {
                plans.combined = byPrice[2];
            }
        }

        for (const accountType of stillUnmapped) {
            if (SubscribeComponent.isAssigned(plans, accountType)) {
                continue;
            }
            if (!plans.low) {
                plans.low = accountType;
            } else if (!plans.high) {
                plans.high = accountType;
            } else if (!plans.combined) {
                plans.combined = accountType;
            }
        }

        return plans;
    }

    private populateAccountTypes(dtos: Array<AccountTypeOverview>) {
        this.isLoading = true;
        this.allAccountTypes = dtos || [];

        if (!dtos || dtos.length === 0) {
            this.plans = {};
            this.isLoading = false;
            return;
        }

        this.plans = this.buildPlansFromApi(dtos);

        this.updateCombinedSavings();

        const combined = this.plans.combined;
        if (combined) {
            this.applySelection(combined, 'combined', false);
        } else if (this.plans.low) {
            this.applySelection(this.plans.low, 'low', false);
        } else if (this.plans.high) {
            this.applySelection(this.plans.high, 'high', false);
        }

        this.isLoading = false;
    }

    private updateCombinedSavings() {
        const low = this.plans.low;
        const high = this.plans.high;
        const combined = this.plans.combined;
        if (low && high && combined) {
            this.combinedSavingsAmount = Math.max(0, low.price + high.price - combined.price);
        } else {
            this.combinedSavingsAmount = 0;
        }
    }

    private applySelection(accountType: AccountTypeOverview, kind: PlanKind, openCheckout: boolean) {
        this.selectedAccountId = accountType.id;
        this.selectedPlanKind = kind;
        localStorage.setItem(this.selectedAccount, this.selectedAccountId.toString());
        this.cardOnly = accountType.durationDays === 0;
        if (openCheckout) {
            this.showCheckout = true;
        }
    }

    private selectByAccountTypeId(accountTypeId: number, openCheckout: boolean) {
        for (const kind of PLAN_DISPLAY_ORDER) {
            const plan = this.plans[kind];
            if (plan && plan.id === accountTypeId) {
                this.applySelection(plan, kind, openCheckout);
                return;
            }
        }

        const accountType = this.allAccountTypes.find((t) => t.id === accountTypeId);
        if (!accountType) {
            return;
        }

        let kind = SubscribeComponent.explicitPlanKind(accountType);
        if (!kind) {
            kind = 'combined';
        }
        if (!this.plans[kind]) {
            this.plans[kind] = accountType;
        }
        this.applySelection(this.plans[kind], kind, openCheckout);
    }

    onSubscribeNow(kind: PlanKind) {
        const accountType = this.plans[kind];
        if (!accountType) {
            return;
        }
        this.applySelection(accountType, kind, true);
        const root = document.querySelector('.subscribe-page-root');
        if (root) {
            root.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    backToPricing() {
        if (this.renewalPayByCard) {
            this.router.navigateByUrl('/account/subscriptions');
            return;
        }
        this.showCheckout = false;
        this.errors = '';
    }

    private resetToPlanSelection() {
        if (this.renewalPayByCard) {
            return;
        }

        this.showCheckout = false;
        this.hideCardPayment = false;
        this.errors = '';
        this.poNumber = '';
        this.agreedToTandCs = false;
        this.paymentMethod = PaymentMethod.Card;

        if (this.plans.combined) {
            this.applySelection(this.plans.combined, 'combined', false);
        } else if (this.plans.low) {
            this.applySelection(this.plans.low, 'low', false);
        } else if (this.plans.high) {
            this.applySelection(this.plans.high, 'high', false);
        }
    }

    paymentMethodChanged(paymentMethod: PaymentMethod) {
    }

    subscribe() {
        this.errors = '';

        if (this.paymentMethod === undefined || this.paymentMethod === null) {
            this.errors = 'You must select a payment method.';
            return;
        } else if (this.paymentMethod === PaymentMethod.Card) {
            this.errors = 'You must complete the card submission form.';
            return;
        } else if (this.paymentMethod === PaymentMethod.BACS && !this.poNumber) {
            this.errors = 'You must enter a PO number or email address for BACS.';
            return;
        }

        if (this.selectedAccountId === undefined || this.selectedAccountId === null) {
            this.errors = 'You must select a subscription option.';
            return;
        }

        if (!this.agreedToTandCs) {
            this.errors = 'You must agree to the terms and conditions.';
            return;
        }

        this.blockUi.start();

        this.subscriptionService.subscribe({
            accountTypeId: this.selectedAccountId,
            paymentMethod: this.paymentMethod,
            customerId: this.loggedInUserService.getCurrentUser().userId,
            additionalInformation: this.poNumber
        }).subscribe(() => {
            this.continueAfterSubscription();
            this.blockUi.stop();
        }, () => { this.blockUi.stop(); });
    }

    cardSubscription(tokenResult) {
        this.errors = '';

        if (this.selectedAccountId === undefined || this.selectedAccountId === null) {
            this.errors = 'You must select a subscription option.';
            return;
        }

        this.subscriptionService.subscribeWithCard({
            accountTypeId: this.selectedAccountId,
            paymentMethod: PaymentMethod.Card,
            customerId: this.loggedInUserService.getCurrentUser().userId,
            automaticRenewal: tokenResult.autoRenew,
            stripeToken: tokenResult.source,
            is3DSecure: tokenResult.threeDSecure,
            clientSecret: tokenResult.clientSecret
        }).subscribe(
            (response: any) => {
                const subscriptionResult = response.value;
                const paymentId = subscriptionResult.paymentId;
                this.paymentService.sendSubscriptionReceipt(paymentId);
                this.authService.startupTokenRefresh();
                this.authService.getNewToken().subscribe(() => {
                    this.loggedInUserService.refreshUserClaims();
                    window.location.href = subscriptionResult.redirectUrl;
                });
            },
            () => {
                this.blockUi.stop();
                this.hideCardPayment = false;
            });
    }

    continueAfterSubscription() {
        this.blockUi.stop();
        if (this.paymentMethod === PaymentMethod.Phone) {
            this.router.navigateByUrl('/account/phone');
        } else if (this.paymentMethod === PaymentMethod.BACS) {
            this.router.navigateByUrl('/account/bacs');
        } else {
            this.router.navigateByUrl('/account/subscriptions');
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/views/subscription/subscribe.component.ts